import { useState, useEffect, useCallback } from 'react';
import { db, type ClipboardPresetRecord } from '../lib/database';

export interface UseClipboardPresetsResult {
  presets: ClipboardPresetRecord[];
  loading: boolean;
  error: string | null;
  addPreset: (name: string, format: string, template: string, isDefault?: boolean) => Promise<string>;
  updatePreset: (id: string, updates: Partial<ClipboardPresetRecord>) => Promise<void>;
  deletePreset: (id: string) => Promise<void>;
  getDefaultPreset: () => ClipboardPresetRecord | undefined;
  setDefaultPreset: (id: string) => Promise<void>;
  applyPreset: (presetId: string, hex: string) => string;
  refresh: () => Promise<void>;
}

const DEFAULT_PRESETS = [
  { name: 'HEX', format: 'hex', template: '{hex}' },
  { name: 'RGB', format: 'rgb', template: 'rgb({r}, {g}, {b})' },
  { name: 'RGBA', format: 'rgba', template: 'rgba({r}, {g}, {b}, {a})' },
  { name: 'HSL', format: 'hsl', template: 'hsl({h}, {s}%, {l}%)' },
  { name: 'CSS Variable', format: 'css-var', template: '--color-primary: {hex};' },
  { name: 'Tailwind', format: 'tailwind', template: "'primary': '{hex}'," },
];

// Module-level flag prevents StrictMode double-seeding.
// Note: If seeding fails partway, the DB may contain a partial set.
// The user can manually add missing presets via UI, or reset by clearing IndexedDB.
let seeding = false;

export function useClipboardPresets(): UseClipboardPresetsResult {
  const [presets, setPresets] = useState<ClipboardPresetRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPresets = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const items = await db.getClipboardPresets();
      
      // Initialize with default presets if empty
      if (items.length === 0 && !seeding) {
        seeding = true;
        try {
          for (const preset of DEFAULT_PRESETS) {
            await db.addClipboardPreset({
              ...preset,
              isDefault: preset.format === 'hex',
            });
          }
        } finally {
          seeding = false;
        }
        const newItems = await db.getClipboardPresets();
        setPresets(newItems);
      } else {
        setPresets(items);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load presets');
      console.error('Error loading clipboard presets:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPresets();
  }, [loadPresets]);

  const addPreset = useCallback(async (
    name: string,
    format: string,
    template: string,
    isDefault: boolean = false
  ): Promise<string> => {
    try {
      // If setting as default, unset other defaults
      if (isDefault) {
        const currentDefault = await db.getDefaultClipboardPreset();
        if (currentDefault) {
          await db.updateClipboardPreset(currentDefault.id!, { isDefault: false });
        }
      }
      
      const id = await db.addClipboardPreset({
        name,
        format,
        template,
        isDefault,
      });
      await loadPresets();
      return id;
    } catch (err) {
      console.error('Error adding preset:', err);
      throw err;
    }
  }, [loadPresets]);

  const updatePreset = useCallback(async (id: string, updates: Partial<ClipboardPresetRecord>) => {
    try {
      // If setting as default, unset other defaults
      if (updates.isDefault) {
        const currentDefault = await db.getDefaultClipboardPreset();
        if (currentDefault && currentDefault.id !== id) {
          await db.updateClipboardPreset(currentDefault.id!, { isDefault: false });
        }
      }
      
      await db.updateClipboardPreset(id, updates);
      await loadPresets();
    } catch (err) {
      console.error('Error updating preset:', err);
      throw err;
    }
  }, [loadPresets]);

  const deletePreset = useCallback(async (id: string) => {
    try {
      await db.deleteClipboardPreset(id);
      await loadPresets();
    } catch (err) {
      console.error('Error deleting preset:', err);
      throw err;
    }
  }, [loadPresets]);

  const getDefaultPreset = useCallback((): ClipboardPresetRecord | undefined => {
    return presets.find(p => p.isDefault) || presets[0];
  }, [presets]);

  const setDefaultPreset = useCallback(async (id: string) => {
    try {
      // Unset current default
      const currentDefault = presets.find(p => p.isDefault);
      if (currentDefault && currentDefault.id !== id) {
        await db.updateClipboardPreset(currentDefault.id!, { isDefault: false });
      }
      
      // Set new default
      await db.updateClipboardPreset(id, { isDefault: true });
      await loadPresets();
    } catch (err) {
      console.error('Error setting default preset:', err);
      throw err;
    }
  }, [presets, loadPresets]);

  const applyPreset = useCallback((presetId: string, hex: string): string => {
    const preset = presets.find(p => p.id === presetId);
    if (!preset) return hex;

    // Parse hex to RGB
    const hexClean = hex.replace('#', '');
    const r = parseInt(hexClean.substring(0, 2), 16);
    const g = parseInt(hexClean.substring(2, 4), 16);
    const b = parseInt(hexClean.substring(4, 6), 16);
    const a = hexClean.length === 8 ? parseInt(hexClean.substring(6, 8), 16) / 255 : 1;

    // Convert to HSL
    const rNorm = r / 255;
    const gNorm = g / 255;
    const bNorm = b / 255;
    const max = Math.max(rNorm, gNorm, bNorm);
    const min = Math.min(rNorm, gNorm, bNorm);
    const l = (max + min) / 2;
    let h = 0;
    let s = 0;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      
      if (max === rNorm) {
        h = ((gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 0)) / 6;
      } else if (max === gNorm) {
        h = ((bNorm - rNorm) / d + 2) / 6;
      } else {
        h = ((rNorm - gNorm) / d + 4) / 6;
      }
    }

    // Apply template
    return preset.template
      .replace('{hex}', hex)
      .replace('{r}', r.toString())
      .replace('{g}', g.toString())
      .replace('{b}', b.toString())
      .replace('{a}', a.toFixed(2))
      .replace('{h}', Math.round(h * 360).toString())
      .replace('{s}', Math.round(s * 100).toString())
      .replace('{l}', Math.round(l * 100).toString());
  }, [presets]);

  return {
    presets,
    loading,
    error,
    addPreset,
    updatePreset,
    deletePreset,
    getDefaultPreset,
    setDefaultPreset,
    applyPreset,
    refresh: loadPresets,
  };
}
